import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import SEOHead, { SeoBreadcrumb } from './SEOHead';
import {
  BRAND_DESCRIPTION,
  BRAND_KEYWORDS,
  BRAND_NAME,
  BRAND_TITLE,
  GRADE_SEO,
} from '../../config/brand';

type PageSeo = {
  title: string;
  description: string;
  keywords?: string;
  noIndex?: boolean;
  breadcrumbs?: SeoBreadcrumb[];
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
};

const withBrandKeywords = (extra?: string) =>
  extra ? `${BRAND_KEYWORDS}, ${extra}` : BRAND_KEYWORDS;

const DEFAULT: PageSeo = {
  title: BRAND_TITLE,
  description: BRAND_DESCRIPTION,
  keywords: BRAND_KEYWORDS,
  breadcrumbs: [],
};

function gradeFromPath(pathname: string, prefix: '/courses/grade/' | '/books/grade/') {
  if (!pathname.startsWith(prefix)) return null;
  return pathname.slice(prefix.length).split('/')[0] || null;
}

/**
 * Resolve SEO for the current path so every public route is indexable with rich metadata.
 */
function resolvePageSeo(pathname: string): PageSeo {
  if (pathname === '/') {
    return {
      title: BRAND_TITLE,
      description: BRAND_DESCRIPTION,
      keywords: withBrandKeywords('تعليم أزهري أونلاين, كورسات سعيد الفقي'),
      breadcrumbs: [],
      structuredData: {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'ما هي منصة نهج الأزهر؟',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'منصة تعليمية أونلاين لطلاب الثانوية الأزهرية تقدّم كورسات في اللغة العربية والعلم الشرعي مع الأستاذ سعيد الفقي.',
            },
          },
          {
            '@type': 'Question',
            name: 'لأي صفوف تقدّم المنصة كورسات؟',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'تقدّم المنصة كورسات للصفوف الأول والثاني والثالث الثانوي الأزهري.',
            },
          },
          {
            '@type': 'Question',
            name: 'هل يمكن لولي الأمر متابعة الطالب؟',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'نعم عبر صفحة متابعة ولي الأمر باستخدام أرقام الهواتف المسجّلة.',
            },
          },
        ],
      },
    };
  }

  if (pathname === '/login') {
    return {
      title: `تسجيل الدخول | ${BRAND_NAME}`,
      description: `سجّل دخولك إلى منصة نهج الأزهر لمتابعة كورساتك وحصصك الأزهرية أونلاين مع الأستاذ سعيد الفقي.`,
      keywords: withBrandKeywords('تسجيل دخول نهج الأزهر'),
      noIndex: true,
      breadcrumbs: [{ name: 'تسجيل الدخول', path: '/login' }],
    };
  }

  if (pathname === '/register') {
    return {
      title: `إنشاء حساب | ${BRAND_NAME}`,
      description: `أنشئ حسابك مجاناً في منصة نهج الأزهر وابدأ مناهج الثانوية الأزهرية واللغة العربية مع سعيد الفقي.`,
      keywords: withBrandKeywords('إنشاء حساب, تسجيل طالب أزهري'),
      noIndex: true,
      breadcrumbs: [{ name: 'إنشاء حساب', path: '/register' }],
    };
  }

  if (pathname === '/forgot-password') {
    return {
      title: 'استعادة كلمة المرور',
      description: 'استعد كلمة مرور حسابك في منصة نهج الأزهر بسهولة وأمان.',
      noIndex: true,
    };
  }

  if (pathname === '/reset-password') {
    return {
      title: 'إعادة تعيين كلمة المرور',
      description: 'أعد تعيين كلمة المرور الخاصة بحسابك في منصة نهج الأزهر.',
      noIndex: true,
    };
  }

  if (pathname === '/contact') {
    return {
      title: `تواصل معنا | ${BRAND_NAME}`,
      description: `تواصل مع منصة نهج الأزهر — واتساب وفيسبوك ويوتيوب الأستاذ سعيد الفقي لدعم طلاب الثانوية الأزهرية.`,
      keywords: withBrandKeywords('تواصل نهج الأزهر, واتساب سعيد الفقي'),
      breadcrumbs: [{ name: 'تواصل معنا', path: '/contact' }],
    };
  }

  if (pathname === '/privacy') {
    return {
      title: `سياسة الخصوصية | ${BRAND_NAME}`,
      description: `سياسة خصوصية منصة نهج الأزهر: جمع واستخدام بيانات طلاب الثانوية الأزهرية مع الأستاذ سعيد الفقي.`,
      keywords: withBrandKeywords('سياسة الخصوصية'),
      breadcrumbs: [{ name: 'سياسة الخصوصية', path: '/privacy' }],
    };
  }

  if (pathname === '/terms') {
    return {
      title: `الشروط والأحكام | ${BRAND_NAME}`,
      description: `شروط استخدام منصة نهج الأزهر مع سعيد الفقي — الحسابات، الاشتراكات، والمحتوى التعليمي الأزهري.`,
      keywords: withBrandKeywords('شروط الاستخدام'),
      breadcrumbs: [{ name: 'الشروط والأحكام', path: '/terms' }],
    };
  }

  if (pathname === '/refund-policy') {
    return {
      title: `سياسة الاسترجاع | ${BRAND_NAME}`,
      description: `سياسة الاسترجاع في منصة نهج الأزهر وشروط طلب استرداد رسوم الكورسات الأزهرية أونلاين.`,
      keywords: withBrandKeywords('سياسة الاسترجاع, استرداد رسوم'),
      breadcrumbs: [{ name: 'سياسة الاسترجاع', path: '/refund-policy' }],
    };
  }

  if (pathname === '/parent-monitoring') {
    return {
      title: `متابعة ولي الأمر | ${BRAND_NAME}`,
      description: `تابع تقدم ابنك في منصة نهج الأزهر مع الأستاذ سعيد الفقي واطلع على نشاطه في الدروس الأزهرية.`,
      keywords: withBrandKeywords('متابعة ولي الأمر, تقدم الطالب الأزهري'),
      breadcrumbs: [{ name: 'متابعة ولي الأمر', path: '/parent-monitoring' }],
    };
  }

  const courseGrade = gradeFromPath(pathname, '/courses/grade/');
  if (courseGrade) {
    const g = GRADE_SEO[courseGrade];
    const gradeLabel = g?.short || g?.title || 'كورسات حسب الصف';
    return {
      title: `كورسات ${gradeLabel} | ${BRAND_NAME}`,
      description: `اشترك في كورسات ${g?.title || gradeLabel} على منصة نهج الأزهر مع سعيد الفقي — لغة عربية وعلوم شرعية أونلاين.`,
      keywords: withBrandKeywords(
        `كورسات ${gradeLabel}, ${g?.keywords || ''}, كورسات أزهرية أونلاين`
      ),
      breadcrumbs: [
        { name: 'الكورسات', path: `/courses/grade/${courseGrade}` },
        { name: g?.title || gradeLabel, path: `/courses/grade/${courseGrade}` },
      ],
    };
  }

  const bookGrade = gradeFromPath(pathname, '/books/grade/');
  if (bookGrade) {
    const g = GRADE_SEO[bookGrade];
    const gradeLabel = g?.short || g?.title || 'كتب حسب الصف';
    return {
      title: `كتب ${gradeLabel} | ${BRAND_NAME}`,
      description: `كتب ومراجع ${g?.title || gradeLabel} من منصة نهج الأزهر — مواد أزهرية مع شرح الأستاذ سعيد الفقي.`,
      keywords: withBrandKeywords(`كتب ${gradeLabel}, مراجع أزهرية, ${g?.keywords || ''}`),
      breadcrumbs: [
        { name: 'الكتب', path: `/books/grade/${bookGrade}` },
        { name: g?.title || gradeLabel, path: `/books/grade/${bookGrade}` },
      ],
    };
  }

  if (pathname.startsWith('/courses/')) {
    return {
      title: `تفاصيل الكورس | ${BRAND_NAME}`,
      description: `شاهد تفاصيل الكورس واشترك على منصة نهج الأزهر مع الأستاذ سعيد الفقي — مناهج الثانوية الأزهرية.`,
      keywords: withBrandKeywords('تفاصيل كورس, كورسات أونلاين, دروس أزهرية'),
      breadcrumbs: [{ name: 'تفاصيل الكورس', path: pathname }],
    };
  }

  if (pathname.startsWith('/units/')) {
    return {
      title: `تفاصيل الحصة | ${BRAND_NAME}`,
      description: `حصة مستقلة على منصة نهج الأزهر — محتوى أزهري أونلاين مع الأستاذ سعيد الفقي لطلاب الأزهر.`,
      keywords: withBrandKeywords('حصة مستقلة, دروس أزهرية, حصص أونلاين'),
      breadcrumbs: [{ name: 'تفاصيل الحصة', path: pathname }],
    };
  }

  const teacherGradeMatch = pathname.match(/^\/teacher\/([^/]+)\/grade\/([^/]+)/);
  if (teacherGradeMatch) {
    const gradeSlug = teacherGradeMatch[2];
    const teacherId = teacherGradeMatch[1];
    const g = GRADE_SEO[gradeSlug];
    const gradeLabel = g?.short || g?.title || 'كورسات حسب الصف';
    return {
      title: `كورسات ${gradeLabel} | ${BRAND_NAME}`,
      description: `اشترك في كورسات ${g?.title || gradeLabel} لهذا المدرس على منصة نهج الأزهر — لغة عربية وعلوم شرعية أونلاين.`,
      keywords: withBrandKeywords(
        `كورسات ${gradeLabel}, ${g?.keywords || ''}, كورسات أزهرية أونلاين`
      ),
      breadcrumbs: [
        { name: 'المدرس', path: `/teacher/${teacherId}` },
        { name: g?.title || gradeLabel, path: pathname },
      ],
    };
  }

  if (pathname.startsWith('/teacher/')) {
    return {
      title: `صفحة المعلم | ${BRAND_NAME}`,
      description: `تعرّف على معلمي منصة نهج الأزهر وكورساتهم في اللغة العربية والعلم الشرعي لطلاب الثانوية الأزهرية.`,
      keywords: withBrandKeywords('معلمون, مدرسين أزهر, سعيد الفقي'),
      breadcrumbs: [{ name: 'المعلمون', path: pathname }],
    };
  }

  if (pathname.startsWith('/subject/')) {
    return {
      title: `المعلمون حسب المادة | ${BRAND_NAME}`,
      description: `اختر المادة وشاهد معلمي نهج الأزهر المتخصصين — لغة عربية وعلوم شرعية مع سعيد الفقي.`,
      keywords: withBrandKeywords('مواد دراسية, معلمون حسب المادة'),
      breadcrumbs: [{ name: 'المواد', path: pathname }],
    };
  }

  if (pathname === '/checkout' || pathname === '/payment-success') {
    return {
      title: pathname === '/checkout' ? 'إتمام الشراء' : 'تم الدفع بنجاح',
      description: 'إتمام عملية الاشتراك في كورسات منصة نهج الأزهر.',
      noIndex: true,
    };
  }

  if (pathname.startsWith('/student-portal')) {
    const portalMap: Record<string, PageSeo> = {
      '/student-portal': {
        title: 'بوابة الطالب - كورساتي وحصصي',
        description: 'بوابة الطالب في نهج الأزهر: كورساتك وحصصك ومتابعة تقدمك التعليمي.',
        noIndex: true,
      },
      '/student-portal/browse': {
        title: 'استكشف الكورسات',
        description: 'استكشف كورسات وحصص منصة نهج الأزهر من داخل بوابة الطالب.',
        noIndex: true,
      },
      '/student-portal/wallet': {
        title: 'محفظتي',
        description: 'إدارة محفظتك وشحن الرصيد في منصة نهج الأزهر.',
        noIndex: true,
      },
      '/student-portal/forum': {
        title: 'المنتدى',
        description: 'منتدى الطلاب في منصة نهج الأزهر للأسئلة والدعم العلمي.',
        noIndex: true,
      },
      '/student-portal/my-grades': {
        title: 'درجاتي',
        description: 'اطّلع على درجاتك ونتائجك في منصة نهج الأزهر.',
        noIndex: true,
      },
    };
    if (portalMap[pathname]) return portalMap[pathname];
    if (pathname.startsWith('/student-portal/teacher/')) {
      return {
        title: 'معلم في بوابة الطالب',
        description: 'كورسات المعلم داخل بوابة طالب نهج الأزهر.',
        noIndex: true,
      };
    }
    return {
      title: 'بوابة الطالب',
      description: 'بوابة الطالب في منصة نهج الأزهر.',
      noIndex: true,
    };
  }

  if (
    pathname.startsWith('/course-player/') ||
    pathname.startsWith('/unit-viewer/') ||
    pathname.includes('/curriculum')
  ) {
    return {
      title: 'مشغل المحتوى',
      description: 'مشاهدة المحتوى التعليمي على منصة نهج الأزهر.',
      noIndex: true,
    };
  }

  if (pathname === '/profile') {
    return {
      title: 'الملف الشخصي',
      description: 'إدارة ملفك الشخصي في منصة نهج الأزهر.',
      noIndex: true,
    };
  }

  // Unknown SPA routes → soft 404 UI with noindex
  return {
    title: `الصفحة غير موجودة | ${BRAND_NAME}`,
    description: `الصفحة غير متوفرة على منصة نهج الأزهر. عد للرئيسية أو تصفّح كورسات الثانوية الأزهرية مع سعيد الفقي.`,
    noIndex: true,
    breadcrumbs: [{ name: 'غير موجود', path: pathname }],
  };
}

/**
 * Global SEO for every route — mount once inside the Router.
 */
const RouteSEO: React.FC = () => {
  // Canonical must be pathname-only — query strings (utm, q, etc.) create
  // "Alternate page with proper canonical tag" entries in Search Console.
  const { pathname } = useLocation();

  const seo = useMemo(() => resolvePageSeo(pathname), [pathname]);

  return (
    <SEOHead
      title={seo.title}
      description={seo.description}
      keywords={seo.keywords ?? BRAND_KEYWORDS}
      url={pathname === '/' ? '/' : pathname.replace(/\/+$/, '')}
      noIndex={seo.noIndex}
      breadcrumbs={seo.breadcrumbs}
      structuredData={seo.structuredData}
    />
  );
};

export default RouteSEO;
